import type {
  FittingSlot,
  StoredShipFitting,
} from "@frontier/content/ship-fitting";
import {
  mountedEquipment,
  fittingCells,
  currentShipFitting,
} from "@frontier/content/ship-fitting";
import { sameEquipmentKey } from "@frontier/content/arsenal";
import type { Arsenal, EquipmentKey } from "@frontier/content/arsenal";
import type { Profile } from "@frontier/protocol/contracts";
import type { EquipmentTier } from "@frontier/content/equipment-tiers";

export type EquipmentContext =
  | { kind: "cell"; slot: number }
  | { kind: "weapon"; slot?: number }
  | { kind: "utility"; slot: number }
  | { kind: "module"; slot: 0 | 1 }
  | { kind: "passive" };
export type EquipmentSelection = Pick<
  Profile,
  | "shipFitting"
  | "loadoutId"
  | "hullId"
  | "weaponId"
  | "moduleId"
  | "secondaryModuleId"
  | "passiveId"
  | "equipmentTiers"
>;

export function contextAccepts(
  context: EquipmentContext,
  key: EquipmentKey,
): boolean {
  return (
    context.kind === "cell" ||
    (context.kind === "utility"
      ? key.kind !== "weapon"
      : context.kind === key.kind)
  );
}
export function fittingSlot(context: EquipmentContext): FittingSlot {
  return context.kind === "cell"
    ? { kind: "cell", index: context.slot }
    : context.kind === "weapon"
      ? { kind: "weapon", index: context.slot ?? 0 }
      : {
          kind: "utility",
          index: context.kind === "passive" ? 2 : context.slot,
        };
}
function fittingCellIndex(
  fitting: StoredShipFitting,
  context: EquipmentContext,
): number | undefined {
  const slot = fittingSlot(context);
  return slot.kind === "cell"
    ? slot.index
    : fitting.format === 1
      ? slot.kind === "weapon"
        ? slot.index
        : fitting.weapons.length + slot.index
      : undefined;
}
export function mountedFocus(
  fitting: StoredShipFitting,
  context: EquipmentContext,
) {
  const index = fittingCellIndex(fitting, context);
  const item = index === undefined ? undefined : fittingCells(fitting)[index];
  return item ? { key: item, tier: item.tier } : null;
}
export function mountedCount(
  profile: EquipmentSelection,
  key: EquipmentKey,
  tier: EquipmentTier,
): number {
  return profile.shipFitting
    ? (mountedEquipment(profile.shipFitting).find(
        ({ item }) => sameEquipmentKey(item, key) && item.tier === tier,
      )?.count ?? 0)
    : Number(equipmentInstalled(profile, key, tier));
}
export function equipmentCount(
  arsenal: Arsenal,
  key: EquipmentKey,
  tier?: EquipmentTier,
): number {
  return arsenal.stacks
    .filter(
      (stack) =>
        sameEquipmentKey(stack.key, key) &&
        (tier === undefined || stack.tier === tier),
    )
    .reduce((sum, stack) => sum + stack.count, 0);
}
export function equipmentInstalled(
  profile: EquipmentSelection,
  key: EquipmentKey,
  tier: EquipmentTier,
): boolean {
  if (profile.shipFitting) return mountedCount(profile, key, tier) > 0;
  return key.kind === "weapon"
    ? profile.weaponId === key.id && profile.equipmentTiers.weapon === tier
    : key.kind === "module"
      ? (profile.moduleId === key.id &&
          profile.equipmentTiers.module === tier) ||
        (profile.secondaryModuleId === key.id &&
          profile.equipmentTiers.secondaryModule === tier)
      : profile.passiveId === key.id && profile.equipmentTiers.passive === tier;
}
export function equippedTier(
  profile: EquipmentSelection,
  context: EquipmentContext,
  key: EquipmentKey,
): EquipmentTier | undefined {
  if (profile.shipFitting) {
    const current = mountedFocus(profile.shipFitting, context);
    return current && sameEquipmentKey(current.key, key)
      ? current.tier
      : undefined;
  }
  if (
    context.kind === "weapon" &&
    key.kind === "weapon" &&
    profile.weaponId === key.id
  )
    return profile.equipmentTiers.weapon;
  if (
    context.kind === "passive" &&
    key.kind === "passive" &&
    profile.passiveId === key.id
  )
    return profile.equipmentTiers.passive;
  if (context.kind === "module" && key.kind === "module") {
    if (context.slot === 0 && profile.moduleId === key.id)
      return profile.equipmentTiers.module;
    if (context.slot === 1 && profile.secondaryModuleId === key.id)
      return profile.equipmentTiers.secondaryModule;
  }
  return undefined;
}
export function equipmentSelection(
  profile: EquipmentSelection,
  context: EquipmentContext,
  key: EquipmentKey | null,
  tier: EquipmentTier = 0,
): EquipmentSelection | undefined {
  const selected = {
    shipFitting: profile.shipFitting,
    loadoutId: profile.loadoutId,
    hullId: profile.hullId,
    weaponId: profile.weaponId,
    moduleId: profile.moduleId,
    secondaryModuleId: profile.secondaryModuleId,
    passiveId: profile.passiveId,
    equipmentTiers: { ...profile.equipmentTiers },
  };
  if (profile.shipFitting) {
    const index = fittingCellIndex(profile.shipFitting, context);
    const fitting = currentShipFitting(profile.shipFitting);
    if (
      index === undefined ||
      index < 0 ||
      index >= fitting.cells.length ||
      (key && !contextAccepts(context, key))
    )
      return undefined;
    fitting.cells[index] = key ? { ...key, tier } : null;
    return { ...selected, shipFitting: fitting };
  }
  if (key === null)
    return context.kind === "passive"
      ? {
          ...selected,
          passiveId: "none",
          equipmentTiers: { ...selected.equipmentTiers, passive: 0 },
        }
      : undefined;
  if (context.kind === "weapon" && key.kind === "weapon")
    return {
      ...selected,
      weaponId: key.id,
      equipmentTiers: { ...selected.equipmentTiers, weapon: tier },
    };
  if (context.kind === "passive" && key.kind === "passive")
    return {
      ...selected,
      passiveId: key.id,
      equipmentTiers: { ...selected.equipmentTiers, passive: tier },
    };
  if (context.kind !== "module" || key.kind !== "module") return undefined;
  if (
    key.id ===
    (context.slot === 0 ? profile.secondaryModuleId : profile.moduleId)
  )
    return undefined;
  return context.slot === 0
    ? {
        ...selected,
        moduleId: key.id,
        equipmentTiers: { ...selected.equipmentTiers, module: tier },
      }
    : {
        ...selected,
        secondaryModuleId: key.id,
        equipmentTiers: { ...selected.equipmentTiers, secondaryModule: tier },
      };
}

export function sameEquipmentContext(
  left: EquipmentContext,
  right: EquipmentContext,
): boolean {
  return (
    left.kind === right.kind &&
    (!("slot" in left) ||
      ("slot" in right && (left.slot ?? 0) === (right.slot ?? 0)))
  );
}
export function equipmentContextLabel(context: EquipmentContext): string {
  return context.kind === "utility" || context.kind === "cell"
    ? `Ячейка ${context.slot + 1}`
    : context.kind === "weapon"
      ? `Оружие ${(context.slot ?? 0) + 1}`
      : context.kind === "passive"
        ? "Усилитель"
        : `Модуль ${context.slot + 1}`;
}
export function installedEquipmentSlot(
  profile: EquipmentSelection,
  key: EquipmentKey,
  tier: EquipmentTier,
): string | null {
  if (profile.shipFitting) {
    const matches = fittingCells(profile.shipFitting).flatMap((item, index) =>
      item && sameEquipmentKey(item, key) && item.tier === tier
        ? [`Ячейка ${index + 1}`]
        : [],
    );
    return matches.length ? matches.join(", ") : null;
  }
  if (!equipmentInstalled(profile, key, tier)) return null;
  return key.kind === "weapon"
    ? "Оружие"
    : key.kind === "passive"
      ? "Усилитель"
      : profile.moduleId === key.id && profile.equipmentTiers.module === tier
        ? "Модуль 1 · Q"
        : "Модуль 2 · 2";
}

/** A consumed workshop source follows the remaining canonical stack; an empty blueprint starts at MK I. */
export function workshopEquipmentTier(
  profile: Pick<Profile, "arsenal"> & EquipmentSelection,
  context: EquipmentContext,
  key: EquipmentKey,
  preferred: EquipmentTier,
): EquipmentTier {
  const owned = profile.arsenal.stacks.filter(
    (stack) => sameEquipmentKey(stack.key, key) && stack.count > 0,
  );
  if (owned.length === 0) return 0;
  if (owned.some((stack) => stack.tier === preferred)) return preferred;
  const current = equippedTier(profile, context, key);
  return (
    owned.find((stack) => stack.tier === current)?.tier ?? owned[0]?.tier ?? 0
  );
}
